import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarHeart, Loader2, Plus, Trash2, X } from 'lucide-react'
import { api, apiErrorMessage } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import { currentLocaleTag } from '../../lib/formatting'
import {
  CLIENT_DATE_KINDS,
  type ClientDateKind,
  type ClientImportantDateDto,
  type ClientImportantDateListResponse,
} from '@toga/shared'

/**
 * Las fechas señaladas de un cliente: su cumpleaños, su aniversario de boda, la
 * graduación de su hija.
 *
 * ☠️ Son datos de PERSONAS QUE NO SON NUESTRO CLIENTE y que no nos los han dado
 * ellas. Están aquí para tratar bien a alguien, no para segmentar: no hay ningún
 * camino desde esta pantalla a la audiencia de una campaña, y es a propósito.
 */
interface Props {
  clientId: string
  canWrite: boolean
  /** Contactos del cliente, para poder colgar la fecha de una persona concreta. */
  contactos?: Array<{ id: string; name: string }>
}

const MESES = 12

export default function ClientDates({ clientId, canWrite, contactos = [] }: Props) {
  const { t } = useTranslation(['clients', 'common'])
  const [items, setItems] = useState<ClientImportantDateDto[]>([])
  const [cargando, setCargando] = useState(true)
  const [anadiendo, setAnadiendo] = useState(false)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    label: '',
    kind: 'CUMPLEANOS' as ClientDateKind,
    day: 1,
    month: 1,
    year: '',
    annual: true,
    contactId: '',
  })

  const reload = useCallback(async () => {
    setCargando(true)
    try {
      const r = await api.get<ClientImportantDateListResponse>(`/clients/${clientId}/dates`)
      setItems(r.items)
    } catch {
      setItems([])
    } finally {
      setCargando(false)
    }
  }, [clientId])

  useEffect(() => {
    void reload()
  }, [reload])

  const guardar = async () => {
    setBusy(true)
    try {
      await api.post(`/clients/${clientId}/dates`, {
        label: form.label,
        kind: form.kind,
        day: form.day,
        month: form.month,
        year: form.year ? Number(form.year) : null,
        annual: form.annual,
        contactId: form.contactId || null,
      })
      setAnadiendo(false)
      setForm({
        label: '',
        kind: 'CUMPLEANOS',
        day: 1,
        month: 1,
        year: '',
        annual: true,
        contactId: '',
      })
      await reload()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('dates.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const borrar = async (d: ClientImportantDateDto) => {
    if (!(await confirmDialog({ message: t('dates.confirmDelete', { name: d.label }), tone: 'danger' })))
      return
    try {
      await api.delete(`/client-dates/${d.id}`)
      await reload()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('dates.errors.generic')))
    }
  }

  const comoDia = (d: ClientImportantDateDto) => {
    if (!d.proximaOcurrencia) return t('dates.past')
    return new Date(d.proximaOcurrencia).toLocaleDateString(currentLocaleTag(), {
      day: 'numeric',
      month: 'long',
    })
  }

  const cuanto = (d: ClientImportantDateDto) => {
    if (d.diasHasta === null) return null
    if (d.diasHasta === 0) return t('dates.today')
    if (d.diasHasta === 1) return t('dates.tomorrow')
    return t('dates.inDays', { n: d.diasHasta })
  }

  const inputCls =
    'w-full px-2.5 py-1.5 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-toga-500/50'

  return (
    <section className="surface overflow-hidden">
      <header className="px-4 py-3 border-b border-base flex items-center gap-2">
        <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
          <CalendarHeart size={15} className="text-rose-500 dark:text-rose-300" />
          {t('dates.title')}
        </h3>
        {canWrite && !anadiendo && (
          <button
            type="button"
            onClick={() => setAnadiendo(true)}
            className="ml-auto text-xs px-2 py-1 rounded-lg t-secondary border border-base hover:bg-hover inline-flex items-center gap-1"
          >
            <Plus size={12} /> {t('dates.add')}
          </button>
        )}
      </header>

      {cargando ? (
        <p className="px-4 py-6 text-center t-muted text-sm">
          <Loader2 size={14} className="inline-block animate-spin" />
        </p>
      ) : (
        <>
          {items.length === 0 && !anadiendo && (
            <p className="px-4 py-6 text-center t-muted text-sm">{t('dates.empty')}</p>
          )}
          {items.length > 0 && (
            <ul className="divide-y divide-base/50">
              {items.map((d) => (
                <li key={d.id} className="px-4 py-2.5 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="t-primary text-sm">
                      {d.label}
                      {d.contactName && <span className="t-faint"> · {d.contactName}</span>}
                    </div>
                    <div className="t-faint text-xs mt-0.5">
                      {comoDia(d)}
                      {cuanto(d) && <span className="t-muted"> · {cuanto(d)}</span>}
                      {d.cumple !== null && <span> · {t('dates.turns', { n: d.cumple })}</span>}
                      {!d.annual && <span> · {t('dates.oneOff')}</span>}
                    </div>
                  </div>
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => void borrar(d)}
                      title={t('common:actions.delete')}
                      className="shrink-0 p-1.5 rounded t-faint hover:bg-hover hover:text-rose-600"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {anadiendo && (
            <div className="px-4 py-3 border-t border-base space-y-2.5 bg-[var(--bg-input-soft)]">
              <div className="flex items-center gap-2">
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder={t('dates.form.labelPlaceholder')}
                  className={inputCls}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setAnadiendo(false)}
                  className="shrink-0 p-1.5 rounded t-faint hover:bg-hover"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as ClientDateKind })}
                  className={inputCls}
                >
                  {CLIENT_DATE_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`dates.kind.${k}`)}
                    </option>
                  ))}
                </select>
                <select
                  value={form.day}
                  onChange={(e) => setForm({ ...form, day: Number(e.target.value) })}
                  className={inputCls}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <select
                  value={form.month}
                  onChange={(e) => setForm({ ...form, month: Number(e.target.value) })}
                  className={inputCls}
                >
                  {Array.from({ length: MESES }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {new Date(2000, n - 1, 1).toLocaleDateString(currentLocaleTag(), {
                        month: 'long',
                      })}
                    </option>
                  ))}
                </select>
                <input
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value.replace(/\D/g, '') })}
                  placeholder={t('dates.form.yearPlaceholder')}
                  inputMode="numeric"
                  maxLength={4}
                  className={inputCls}
                />
              </div>

              {contactos.length > 0 && (
                <select
                  value={form.contactId}
                  onChange={(e) => setForm({ ...form, contactId: e.target.value })}
                  className={inputCls}
                >
                  <option value="">{t('dates.form.noContact')}</option>
                  {contactos.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}

              <label className="flex items-center gap-2 text-xs t-secondary">
                <input
                  type="checkbox"
                  checked={form.annual}
                  onChange={(e) => setForm({ ...form, annual: e.target.checked })}
                  className="accent-toga-600"
                />
                {t('dates.form.annual')}
              </label>
              {/* Sin año no se sabe de qué día habla, y el aviso no sabría cuándo
                  saltar. Se dice aquí en vez de dejar que lo rechace el servidor. */}
              {!form.annual && !form.year && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  {t('dates.form.needsYear')}
                </p>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={busy || !form.label.trim() || (!form.annual && !form.year)}
                  onClick={() => void guardar()}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                  {t('common:actions.save')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  )
}
